import React, { useCallback, useMemo, useRef, type ReactNode } from 'react';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import { useLayoutEffect } from '@atlaskit/ds-lib/use-layout-effect';
import { MenuListItem } from '@atlaskit/navigation-system/top-nav-items';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ConversationAssistantButtonNav4 } from '@atlassian/conversation-assistant-ui-components/ui/ConversationAssistantButton';
import type { AnyEntryPoint } from '@atlassian/entry-point-types/types';
import { PanelTrigger, type PanelTriggerRenderProps } from '@atlassian/entry-points/panel-trigger';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import type { ChatProps } from '../../common/types';

/**
 * Platform `ChatComponent` provided through the Global Top Navigation `ChatSlot`.
 *
 * Chat panel content is supplied as an entry point and is preloaded on interaction.
 * GlobalRoot renders the loaded content in a responsive, resizable, page-layout end panel.
 * The `onClick` allows an app to opt-out of the GlobalRoot chat panel and delegate to their
 * own.
 *
 * With `platform-dst-chat-panel-layout` enabled, the trigger stays mounted but
 * hides while `isChatPanelOpen` is true, then restores focus after the panel closes.
 *
 * Supports all of the underlying props for the ConversationAssistantButtonNav4.
 */
export function Chat<TEntryPoint extends AnyEntryPoint>({
	app,
	closeChatPanel,
	entryPoint,
	entryPointParams,
	entryPointProps,
	errorFallback,
	fallback,
	isChatPanelOpen,
	onClick,
	openChatPanel,
	triggerRef,
	...props
}: ChatProps<TEntryPoint>): JSX.Element | null {
	const isSelfHiding = fg('platform-dst-chat-panel-layout');
	const buttonRef = useRef<HTMLButtonElement>(null);
	const mergedTriggerRef = useMemo(() => mergeRefs([buttonRef, triggerRef]), [triggerRef]);
	const wasHidden = useRef(false);
	const isHidden = isSelfHiding && Boolean(isChatPanelOpen);

	useLayoutEffect(() => {
		// React has removed `hidden` before this effect runs. Never move focus on
		// initial render or on an open request that did not make the sidebar visible.
		if (isSelfHiding && wasHidden.current && !isHidden) {
			const button = buttonRef.current;
			const activeElement = button?.ownerDocument.activeElement;
			// Removing the focused panel leaves focus on the document body. If a
			// different control dismissed it (such as SideNavToggle), keep focus there.
			if (button && (!activeElement || activeElement === button.ownerDocument.body)) {
				button.focus();
			}
		}
		wasHidden.current = isHidden;
	}, [isHidden, isSelfHiding]);

	const { fireEvent } = useAnalyticsEvents();
	const fireChatClicked = useCallback(
		(slotType: 'button' | 'panel') => {
			fireEvent('ui.topNav.clicked.chat', { slotType });
		},
		[fireEvent],
	);

	const handleControlledClick = useCallback(
		(event: React.MouseEvent<HTMLElement, MouseEvent>, uiAnalyticsEvent: UIAnalyticsEvent) => {
			fireChatClicked('button');
			onClick?.(event, uiAnalyticsEvent);
		},
		[fireChatClicked, onClick],
	);

	const onOpenPanel = useCallback(
		(content: ReactNode) => {
			openChatPanel?.(content);
		},
		[openChatPanel],
	);

	const onClosePanel = useCallback(() => {
		closeChatPanel?.();
	}, [closeChatPanel]);

	// A self-hiding action is never selected. Keeping isActive false also avoids
	// switching AI Mate's selected/unselected markup and replacing the DOM button.
	const renderTrigger = useCallback(
		({ isSelected: panelIsOpen, onClick: onTriggerClicked, ref }: PanelTriggerRenderProps) => {
			return (
				<ConversationAssistantButtonNav4
					{...props}
					ref={ref}
					isActive={isSelfHiding ? false : (isChatPanelOpen ?? panelIsOpen)}
					onClick={() => {
						fireChatClicked('panel');
						onTriggerClicked();
					}}
					productKey={app}
				/>
			);
		},
		[app, fireChatClicked, isChatPanelOpen, isSelfHiding, props],
	);

	const trigger = entryPoint ? (
		<PanelTrigger
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			errorFallback={errorFallback}
			fallback={fallback}
			isPanelOpen={isChatPanelOpen ?? false}
			onClosePanel={onClosePanel}
			onOpenPanel={onOpenPanel}
			trigger={renderTrigger}
			triggerRef={mergedTriggerRef}
		/>
	) : (
		<ConversationAssistantButtonNav4
			{...props}
			ref={mergedTriggerRef}
			isActive={isSelfHiding ? false : (isChatPanelOpen ?? false)}
			productKey={app}
			onClick={handleControlledClick}
		/>
	);

	// Keep AI Mate's analytics, onboarding and trigger state mounted. `hidden`
	// removes the whole group from layout, the accessibility tree and keyboard order.
	// Own the group here so an ineligible ChatSlot returning null leaves no wrapper.
	return isSelfHiding ? (
		<div role="list" hidden={isHidden}>
			<MenuListItem>{trigger}</MenuListItem>
		</div>
	) : (
		<MenuListItem>{trigger}</MenuListItem>
	);
}
